import { useDisclosure } from '@chakra-ui/react';
import React, { useCallback, useMemo, useState } from 'react';

import { finleyAssert } from '@finley-technologies/common';

import { AthenaTableGQLType } from 'types/dashboard-core-gqlTypes.generated';

import { AddCollateralDataQualityCheckModal } from './AddCollateralDataQualityCheckModal';
import { cdqPromptSuggestions } from './utils';
import { useAthenaMonacoEditor } from '~/packages/athena/hooks/useAthenaMonacoEditor';
import {
  UseFinleyAiThreadsInputs,
  UseFinleyAiThreadsOutput,
} from '~/packages/finleyAi/hooks/useFinleyAiThreads';
import { FinleyAiAthenaSqlGenerationDrawerChatView } from '~/packages/finleyAi/ui/FinleyAiAthenaSqlGenerationDrawerChatView';
import { OnClickSqlAction } from '~/packages/finleyAi/ui/FinleyAiThreadComponents';

type CollateralDataQualityCheckFinleyAiChatContainerProps = {
  organizationId: string;
  initialQuery: string;
  athenaTables: AthenaTableGQLType[];
  onConfirmAddCdq: (params: { name: string; query: string }) => Promise<void>;
  isAddLoading: boolean;
  finleyAiThreadInputs: UseFinleyAiThreadsInputs;
  finleyAiThreadOutputs: UseFinleyAiThreadsOutput;
  onClickEditFinleyAiSql: (sqlQueryString: string) => void;
};
export const CollateralDataQualityCheckFinleyAiChatContainer = ({
  organizationId,
  initialQuery,
  athenaTables,
  onConfirmAddCdq,
  isAddLoading,
  finleyAiThreadInputs,
  finleyAiThreadOutputs,
  onClickEditFinleyAiSql,
}: CollateralDataQualityCheckFinleyAiChatContainerProps) => {
  useAthenaMonacoEditor({ athenaTables });

  const {
    isOpen: isSaveCdqModalOpen,
    onOpen: onSaveCdqModalOpen,
    onClose: onSaveCdqModalClose,
  } = useDisclosure();

  const [selectedQuery, setSelectedQuery] = useState<string>(initialQuery);

  const {
    selectedThread,
    isThreadLoading,
    viewContext,
    isThreadFailed,
    onSendPrompt,
    handleAthenaSqlExplain,
    isProducingMessage,
  } = finleyAiThreadOutputs;

  const { prompt, tempPrompt, setPrompt } = finleyAiThreadInputs;

  finleyAssert(
    viewContext.type === 'athenaSqlGeneration',
    'Sql Generation Finley AI can only be used for athenaSqlGeneration type',
  );

  const onConfirmAdd = useCallback(
    async (name: string) => {
      return onConfirmAddCdq({ name, query: selectedQuery });
    },
    [onConfirmAddCdq, selectedQuery],
  );

  const onClickSqlAction: OnClickSqlAction = useMemo(() => {
    return {
      label: 'Save',
      callback: (querySqlString) => {
        setSelectedQuery(querySqlString);
        onSaveCdqModalOpen();
      },
    };
  }, [onSaveCdqModalOpen]);

  const onClickSecondarySqlAction: OnClickSqlAction = useMemo(() => {
    return {
      label: 'Edit SQL',
      callback: (querySqlString) => {
        onClickEditFinleyAiSql(querySqlString);
      },
    };
  }, [onClickEditFinleyAiSql]);

  const onClickPromptSuggestion = useCallback(
    (rawPromptSuggestion: string) => {
      setPrompt(rawPromptSuggestion);
      onSendPrompt(rawPromptSuggestion);
    },
    [setPrompt, onSendPrompt],
  );

  return (
    <>
      {isSaveCdqModalOpen && (
        <AddCollateralDataQualityCheckModal
          isOpen={isSaveCdqModalOpen}
          onClose={onSaveCdqModalClose}
          onConfirmAdd={onConfirmAdd}
          isAddLoading={isAddLoading}
        />
      )}
      <FinleyAiAthenaSqlGenerationDrawerChatView
        viewContext={viewContext}
        organizationId={organizationId}
        thread={selectedThread}
        isThreadDisabled={isThreadFailed}
        isThreadFailed={isThreadFailed}
        isProducingMessage={isProducingMessage}
        isGeneratingPromptResponse={isThreadLoading}
        handleAthenaSqlExplain={handleAthenaSqlExplain}
        prompt={prompt}
        setPrompt={setPrompt}
        onSendPrompt={onSendPrompt}
        onClickSqlAction={onClickSqlAction}
        onClickSecondarySqlAction={onClickSecondarySqlAction}
        tempPrompt={tempPrompt}
        threadStarterText="What quality check do you want?"
        promptSuggestions={{
          promptSuggestions: cdqPromptSuggestions,
          onClickPromptSuggestion,
        }}
      />
    </>
  );
};
